import h from 'hyperscript';

import { on } from '@disy/lib/event-util';
import { uniqueId } from '@disy/lib/unique-id';
import type { ResponsiveTable } from '@disy/ui.responsive-table/responsive-table';
import { TableEvents } from '@disy/ui.responsive-table/responsive-table-constants';
import { PaginationControlStepper } from '@disy/ui/pagination-control-stepper';
import type { SearchInput } from '@disy/ui/search-input';
import { vr } from '@disy/ui/separator';
import type { ToolbarContent, ToolbarItem } from '@disy/ui/toolbar';
import { Toolbar, createMediumBorderButton } from '@disy/ui/toolbar';

import './responsive-table-toolbar.css';
import i18n from './responsive-table-toolbar.properties';

const COMPONENT_NAME = 'd-responsive-table-toolbar';

export class ResponsiveTableToolbar extends HTMLElement {
  static get observedAttributes() {
    return ['current-page', 'page-count'];
  }

  readonly _table;

  readonly _rowCountSpan;
  readonly _selectedRowCountSpan;
  readonly _heading;
  readonly _toolbar;
  readonly _searchInput?: SearchInput;
  readonly _selectedRowCountTextSupplier;
  readonly _paginationStepper: PaginationControlStepper;

  /**
   * Toolbar for the responsive table component
   *
   * @param table - Instance of ResponsiveTable this toolbar should refer to.
   * @param [options]
   * @param [options.heading] - This is to be used on pages where there are multiple tables
   *     on one page, or a table and some other elements. In these cases the table might need a
   *     caption. The total result count is shown in parentheses after the caption then.
   * @param [options.items] - Toolbar items
   * @param [options.searchInput] - searchInput which will be statically included in the toolbar
   * @param options.showRowCount - indicates that header will contain the row count or not
   * @param options.itemIconOnly - render toolbar items as icon only buttons
   * @param options.selectedRowCountTextSupplier - optional function that provides the text to
   *     override the default text of selected row count
   */
  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    table: ResponsiveTable<any>,
    {
      heading,
      items,
      searchInput,
      showRowCount = true,
      itemIconOnly = false,
      selectedRowCountTextSupplier = (rowCount) => i18n('table.selectedRows', { count: rowCount }),
      paginationStepperAriaControls = '',
    }: ResponsiveTableToolbarOptions,
  ) {
    super();
    this._table = table;

    this._rowCountSpan = h('span');
    this._selectedRowCountSpan = h(`span.${COMPONENT_NAME}--selected-row-counter`);
    this._selectedRowCountTextSupplier = selectedRowCountTextSupplier;
    this._searchInput = searchInput;
    this._paginationStepper = this.#createPaginationStepper(paginationStepperAriaControls);

    if (heading) {
      this._heading = h(`h2.${COMPONENT_NAME}--table-heading`, { id: uniqueId() }, [
        heading,
        h('span', { attrs: { 'aria-hidden': 'true' } }, ...(showRowCount ? [' (', this._rowCountSpan, ')'] : [])),
      ]);
    }

    if (items || this._searchInput) {
      const toolbarContent = items ?? [];
      this._toolbar = new Toolbar(
        this._searchInput ? getToolbarContentWithSearchInput(this._searchInput, toolbarContent) : toolbarContent,
        {
          defaultItemRenderer: (item: ToolbarItem, options?: { iconOnly: boolean }) =>
            createMediumBorderButton(item, Object.assign({ iconOnly: itemIconOnly }, options ?? {})),
        },
      );
    }

    on(this._table, [TableEvents.REFRESH, TableEvents.SELECT], () => this._onTableUpdate());
    if (this._searchInput) {
      on(this._searchInput, 'input', () => this._table.refresh());
    }
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-stack-h', 'space-4');

    this.innerHTML = '';
    this.append(
      h(`.${COMPONENT_NAME}--left.d-stack-h.space-2`, [
        this._heading ?? this._rowCountSpan,
        this._selectedRowCountSpan,
        vr(),
      ]),
    );
    if (this._toolbar) {
      this._toolbar.classList.add(`${COMPONENT_NAME}--right`);
      this.append(this._toolbar);
    }
    this.append(this._paginationStepper);

    if (this._heading) {
      this._table.querySelector('table')!.setAttribute('aria-labelledby', this._heading.id);
    }

    this.#alignStepperAttributes();
  }

  attributeChangedCallback() {
    this.#alignStepperAttributes();
  }

  #alignStepperAttributes() {
    alignAttributeValue(this, 'current-page', this._paginationStepper, 'current-page');
    alignAttributeValue(this, 'page-count', this._paginationStepper, 'page-count');
  }

  refresh() {
    this._onTableUpdate();
  }

  _onTableUpdate() {
    const rowCount = this._table.rowCount;
    this._rowCountSpan.textContent = this._heading ? String(rowCount) : i18n('table.totalRows', { count: rowCount });

    const selectedRowCount = this._table.selectedRows.length;
    this._selectedRowCountSpan.textContent =
      selectedRowCount ? this._selectedRowCountTextSupplier(selectedRowCount) : '';
    this._toolbar?.refresh();
  }

  #createPaginationStepper(ariaControls: string) {
    const paginationControlStepper = new PaginationControlStepper();
    paginationControlStepper.setAttribute('aria-controls', ariaControls);
    paginationControlStepper.hidden = true;
    return paginationControlStepper;
  }

  hidePaginationStepper(hide: boolean) {
    this._paginationStepper.hidden = hide;
  }
}

customElements.define(COMPONENT_NAME, ResponsiveTableToolbar);

function getToolbarContentWithSearchInput(searchInput: SearchInput, toolbarContent: ToolbarContent): ToolbarContent {
  if (toolbarContent instanceof Promise) {
    return toolbarContent.then((toolbarContent) => [searchInput, ...toolbarContent]);
  } else if (typeof toolbarContent === 'function') {
    return () => [searchInput, ...toolbarContent()];
  } else {
    return [searchInput, ...toolbarContent];
  }
}

export interface ResponsiveTableToolbarOptions {
  readonly showRowCount?: boolean;
  readonly heading?: string;
  readonly items?: ToolbarContent;
  readonly itemIconOnly?: boolean;
  readonly searchInput?: SearchInput;
  readonly selectedRowCountTextSupplier?: (rowCount: number) => string;
  paginationStepperAriaControls?: string;
}

function alignAttributeValue(
  source: Element,
  sourceAttributeName: string,
  target: Element,
  targetAttributeName: string,
) {
  const value = source.getAttribute(sourceAttributeName);
  if (value === null) {
    target.removeAttribute(targetAttributeName);
  } else {
    target.setAttribute(targetAttributeName, value);
  }
}
